import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Row, Space } from 'antd';
import { hasPermissions } from 'config/roles/hasPermissions';
import { ListPayload } from 'core/http/response';
import Notification from 'components/ui/base/notification/Notification';
import InvoicesStatusSegmented from 'views/invoices/components/InvoicesStatusSegmented';
import useSession from 'hooks/useSession';
import useTeams from 'hooks/useTeams';
import { Invoice, ListInvoicesSortParams } from 'store/invoices/types';
import { listTeamInvoices, listTeamInvoicesCounts } from 'store/teams/client';
import InvoicesTable from 'views/invoices/dashboard/components/InvoicesTable';
import useInvoicesFilters from 'views/invoices/invoice-details/useInvoicesFilters';
import { getErrorMessage } from 'utils/getErrorMessage';
import { getCustomer } from 'store/customers/client';
import useDebounce from 'utils/useDebounce';
import useCustomers from 'hooks/useCustomers';
import CustomerAutocomplete from 'components/new-ui/composed/selects/CustomerAutocomplete';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import DatePicker from 'components/new-ui/base/date-picker/DatePicker';
import moment from 'moment';
import PopoverButtonsGroup from 'components/new-ui/composed/popover-buttons-group/PopoverButtonsGroup';
import Button from 'components/new-ui/base/button/Button';
import { StyledPopoverButton } from 'components/new-ui/composed/popover-buttons-group/styles';
import { getTableChangeParams, SortDirection, SortDirections } from 'utils/tableHelpers';
import { StyledTag } from 'components/new-ui/base/tag/styles';
import useDuplicateInvoice from 'views/invoices/hooks/useDuplicateInvoice';
import { DashboardInvoicesData, initialInvoicesDashboardData } from 'views/invoices/types';
import { mapInvoiceStatusCountsToDashboardData } from 'views/invoices/utils';
import { useExportInvoice } from 'views/invoices/hooks/useExportInvoice';
import { useArchiveInvoice } from 'views/invoices/hooks/useArchiveInvoice';

const InvoicesTab = () => {
    const { teamId } = useParams();
    const { archivationStatus, setArchivationStatus, getTeamInvoicesCount } = useTeams(teamId);
    const { userInfo } = useSession();
    const { customers, listCustomers } = useCustomers();

    const navigate = useNavigate();
    const {
        queryInputVal,
        tags,
        appliedParams,
        setStatuses,
        setArchived,
        setPage,
        setSize,
        clearFilter,
        clearAll,
        setDate,
        setQueryInputVal,
        setSortState,
        setCustomerId,
    } = useInvoicesFilters();
    const { duplicateInvoice } = useDuplicateInvoice();
    const { exportInvoice } = useExportInvoice();
    const { archiveInvoice, unarchiveInvoice } = useArchiveInvoice(async () => {
        await getInvoices();
        await fetchStatusCounts();
        if (teamId) getTeamInvoicesCount(teamId);
    });

    const [customerLabelById, setCustomerLabelById] = useState<Record<string, string>>({});
    const [invoicesResponse, setInvoicesResponse] = useState<ListPayload<Invoice>>();
    const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
    const [customerInputValue, setCustomerInputValue] = useState<string | undefined>(undefined);
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [showBulkExportPopover, setShowBulkExportPopover] = useState(false);
    const [invoicesCounts, setInvoicesCounts] = useState<DashboardInvoicesData>(initialInvoicesDashboardData);
    const [hasUserEditedCustomerInput, setHasUserEditedCustomerInput] = useState(false);

    const { archived, page, size, sort, sortDirection, exactDate } = appliedParams;

    const canUpdateInvoices = hasPermissions(userInfo, 'invoices', 'UPDATE');

    const upsertCustomerLabel = (id: string, label: string) => {
        setCustomerLabelById((prev) => ({ ...prev, [id]: label }));
    };

    const querySearchCustomers = useCallback(
        async (query: string) => {
            if (query) {
                const params = { query };
                await listCustomers(params);
            } else {
                await listCustomers({ size: 200 });
            }
        },
        [listCustomers]
    );

    const debouncedSearchCustomers = useDebounce(querySearchCustomers, 500);

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys.map((k) => k.toString()));
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const handleSelectCustomer = async (id: string) => {
        const c = customers.list.find((x) => x.id === id);
        const label = c?.name ?? c?.email ?? c?.contact?.name ?? id;

        setHasUserEditedCustomerInput(false);
        setCustomerInputValue(label);
        setCustomerId(id);
        upsertCustomerLabel(id, label);
        setPage(0);
    };

    const handleSearchCustomers = (val: string) => {
        setHasUserEditedCustomerInput(true);
        setCustomerInputValue(val);

        if (!val) {
            setCustomerId(undefined);
            setPage(0);
        }

        debouncedSearchCustomers(val);
    };

    const fetchStatusCounts = useCallback(async () => {
        if (!teamId) {
            return Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Export',
                description: 'Missing team information.',
            });
        }

        try {
            const data = await listTeamInvoicesCounts(teamId, !!archived);
            setInvoicesCounts(mapInvoiceStatusCountsToDashboardData(data.list));
        } catch (error) {
            const err = getErrorMessage(error);
            Notification({
                type: 'error',
                messageTitle: 'Error',
                description: err || 'Could not get invoices counts from the server.',
                placement: 'top',
            });
        }
    }, [archived, teamId]);

    const handleBulkExportPdfAsZip = () => exportInvoice({ type: 'bulk-pdf-zip', invoiceIds: selectedRowKeys });
    const handleBulkExportCsv = () => exportInvoice({ type: 'bulk-csv', invoiceIds: selectedRowKeys });

    const handleExportCsv = async (invoiceId: string, orderNumber: string) => {
        exportInvoice({ type: 'csv', invoiceId, orderNumber });
    };

    const handleDownloadPdf = async (invoiceId: string, orderNumber: string) => {
        exportInvoice({ type: 'pdf', invoiceId, orderNumber });
    };

    const handleDuplicateInvoice = async (invoiceId: string) => {
        duplicateInvoice(invoiceId);
    };

    const getInvoices = useCallback(async () => {
        if (!teamId) return;

        try {
            setIsLoadingInvoices(true);
            const data = await listTeamInvoices(teamId, appliedParams);
            setInvoicesResponse(data);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            Notification({
                type: 'error',
                messageTitle: 'Error',
                description: errorMessage || 'Could not get invoices data',
                placement: 'top',
            });
        } finally {
            setIsLoadingInvoices(false);
        }
    }, [appliedParams, teamId]);

    const hasSelected = selectedRowKeys.length > 0;

    useEffect(() => {
        fetchStatusCounts();
    }, [fetchStatusCounts]);

    useEffect(() => {
        getInvoices();
    }, [getInvoices]);

    // Sync the Archived switch filter with the URL params
    useEffect(() => {
        setArchived(archivationStatus === 'archived' ? true : false);
    }, [appliedParams.archived, archivationStatus, setArchivationStatus, setArchived]);

    useEffect(() => {
        if (!appliedParams.customerId) {
            setHasUserEditedCustomerInput(false);
            setCustomerInputValue(undefined);
        }
    }, [appliedParams.customerId]);

    useEffect(() => {
        const id = appliedParams.customerId;

        if (!id) return;

        if (!customerLabelById[id]) {
            getCustomer(id).then((c) => {
                const label = c.name ?? c.email ?? id;
                upsertCustomerLabel(id, label);

                // Only apply if user is not editing manually
                setCustomerInputValue((current) => (hasUserEditedCustomerInput ? current : label));
            });

            return;
        }

        if (!hasUserEditedCustomerInput) {
            setCustomerInputValue(customerLabelById[id]);
        }
    }, [appliedParams.customerId, customerLabelById, hasUserEditedCustomerInput]);

    return (
        <Space style={{ width: '100%' }} direction="vertical" size={16}>
            <InvoicesStatusSegmented
                value={appliedParams?.listInvoiceStatus[0] || 'ALL'}
                counts={invoicesCounts}
                archived={!!archived}
                onChange={(val) => {
                    if (val === 'ALL') {
                        setStatuses([]);
                        setPage(0);
                    } else {
                        setStatuses([val]);
                        setPage(0);
                    }
                }}
            />

            <Row justify="space-between">
                <Space>
                    <SearchBar
                        inputPlaceholder="Search invoices"
                        value={queryInputVal}
                        onSearch={(val) => {
                            setQueryInputVal(val);
                            setPage(0);
                        }}
                        onClear={() => {
                            setQueryInputVal('');
                            setPage(0);
                        }}
                    />
                    <CustomerAutocomplete
                        placeholder="Filer by customer"
                        value={customerInputValue}
                        inputStyle={{ width: '100%' }}
                        customers={customers.list}
                        handleSelectCustomer={handleSelectCustomer}
                        search={handleSearchCustomers}
                        setCustomer={setCustomerId}
                        disabled={false}
                        dropdownMatchSelectWidth={false}
                        onClear={() => {
                            setCustomerInputValue(undefined);
                            setCustomerId(undefined);
                        }}
                    />
                </Space>

                <Space>
                    <DatePicker
                        value={exactDate ? moment(exactDate, 'YYYY-MM-DD') : undefined}
                        onChange={(val) => {
                            if (!val) {
                                setDate(undefined);
                                return;
                            }
                            setDate(val.format('YYYY-MM-DD'));
                        }}
                    />
                    {canUpdateInvoices && (
                        <PopoverButtonsGroup
                            buttonEl={<Button disabled={!hasSelected}>Export</Button>}
                            open={showBulkExportPopover}
                            onOpenChange={(next) => setShowBulkExportPopover(next)}
                        >
                            <StyledPopoverButton
                                onClick={() => {
                                    handleBulkExportCsv();
                                    setShowBulkExportPopover(false);
                                }}
                            >
                                Export CSV
                            </StyledPopoverButton>
                            <StyledPopoverButton
                                onClick={() => {
                                    handleBulkExportPdfAsZip();
                                    setShowBulkExportPopover(false);
                                }}
                            >
                                Export PDF
                            </StyledPopoverButton>
                        </PopoverButtonsGroup>
                    )}
                </Space>
            </Row>

            <Row>
                {tags.map((t) => {
                    return (
                        <StyledTag
                            key={`${t.key}-${t.value}`}
                            closable
                            onClose={(e) => {
                                e.preventDefault();
                                clearFilter(t.key);
                            }}
                        >
                            {t.value}
                        </StyledTag>
                    );
                })}

                {tags?.length > 0 && (
                    <Button type="link" onClick={clearAll}>
                        Clear All Filters
                    </Button>
                )}
            </Row>

            <InvoicesTable
                loading={isLoadingInvoices}
                onRow={(record) => ({
                    onClick: (e) => {
                        const target = e.target as HTMLElement;
                        if (target.closest('.no-row-nav')) {
                            return;
                        }
                        navigate(`/invoices/${record.id}`);
                    },
                    style: { cursor: 'pointer' },
                })}
                data={invoicesResponse?.list || []}
                showActions={canUpdateInvoices}
                actions={{
                    archive: (invoiceId) => archiveInvoice(invoiceId),
                    unarchive: (invoiceId) => unarchiveInvoice(invoiceId),
                    downloadPdf: (invoiceId, orderNumber) => handleDownloadPdf(invoiceId, orderNumber),
                    duplicate: (invoiceId) => handleDuplicateInvoice(invoiceId),
                    exportCsv: (invoiceId, orderNumber) => handleExportCsv(invoiceId, orderNumber),
                }}
                pagination={{
                    current: page + 1,
                    pageSize: size,
                    total: invoicesResponse?.total,
                    position: ['bottomCenter'],
                    showSizeChanger: true,
                    hideOnSinglePage: true,
                }}
                onChange={(antPagination, _filters, sorter) => {
                    const raw = getTableChangeParams({ pagination: antPagination, sorter });

                    const { page, size, sortParameter, sortDirection } = (raw || {}) as {
                        page?: number;
                        size?: number;
                        sortParameter?: string;
                        sortDirection?: SortDirection;
                    };

                    setPage((p) => (typeof page === 'number' ? page : p));
                    setSize((p) => (typeof size === 'number' ? size : p));

                    if (!sortParameter || !sortDirection) {
                        setSortState({ sort: undefined, sortDirection: undefined });
                    } else {
                        setSortState({
                            sort: sortParameter as ListInvoicesSortParams,
                            sortDirection: sortDirection as SortDirections,
                        });
                    }
                }}
                rowSelection={canUpdateInvoices ? rowSelection : undefined}
                currentSortDirection={sortDirection}
                currentSortParam={sort}
            />
        </Space>
    );
};

export default InvoicesTab;
